/**
 * OTS Commission Calculator
 *
 * Business Configuration (Default values - can be changed by Super Admin):
 * - Commission rates are set by admin and fetched from API
 * - Late Payment Fee: Set by admin
 * - Grace Period: Set by admin
 */

import moment from "moment";

// A bill's dueDate arrives as a "DD-MM-YYYY" string. `new Date("05-09-2026")`
// silently misreads that as MM-DD-YYYY (or produces Invalid Date for any day
// past the 12th), so every parse in this file goes through moment with an
// explicit format instead of the native Date constructor.
const parseDueDate = (value) => {
  if (value instanceof Date) return value;
  const parsed = moment(value, ["DD-MM-YYYY", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.toDate() : new Date(NaN);
};

// Default rates (used as fallback if settings not loaded)
// Set to 0 to avoid showing hardcoded values before API returns
export const DEFAULT_COMMISSION_RATES = {
  ADVANCE: 0, // Will be set by API
  MONTHLY_RENT: 0, // Will be set by API
  LATE_FEE: 0, // Will be set by API
  GRACE_PERIOD: 5, // 5 days grace period
};

// This will be updated dynamically from Redux state
// Components should use getCommissionRates() instead of accessing directly
export let COMMISSION_RATES = { ...DEFAULT_COMMISSION_RATES };

/**
 * Update commission rates from system settings
 * @param {object} settings - Settings from Redux state
 */
export const updateCommissionRates = (settings) => {
  if (settings) {
    COMMISSION_RATES = {
      // Use the actual values from settings - no fallbacks to hardcoded values
      ADVANCE: (settings.advanceCommission ?? 0) / 100,
      MONTHLY_RENT: (settings.rentCommission ?? 0) / 100,
      LATE_FEE: settings.lateFee ?? 0,
      GRACE_PERIOD: settings.gracePeriod ?? 5,
      IS_ACTIVE: settings.isServiceActive ?? true,
    };
  }
  return COMMISSION_RATES;
};

/**
 * Get current commission rates
 * @returns {object} Current commission rates
 */
export const getCommissionRates = () => COMMISSION_RATES;

/**
 * Calculate advance payment breakdown
 * @param {number} rentAmount - Base advance amount
 * @param {object} customRates - Optional custom rates (overrides global)
 * @returns {object} Payment breakdown
 */
export const calculateAdvancePayment = (rentAmount, customRates = null) => {
  const rates = customRates || COMMISSION_RATES;
  const amount = Number(rentAmount) || 0;

  // If service fee is disabled, no commission
  const isActive = rates.IS_ACTIVE !== false;
  const commissionRate = isActive ? rates.ADVANCE || 0 : 0;
  const commissionAmount = Math.round(amount * commissionRate);

  return {
    baseAmount: amount,
    commissionRate: commissionRate,
    commissionPercentage: `${commissionRate * 100}%`,
    commissionAmount: commissionAmount,
    lateFee: 0,
    totalTenantPays: amount + commissionAmount,
    landlordReceives: amount,
    otsRevenue: commissionAmount,
    type: "advance",
    isServiceActive: isActive,
  };
};

/**
 * Calculate monthly rent payment breakdown
 *
 * Late charges belong to the property, not to the platform. When `flatDetail`
 * is supplied, the owner's own settings decide everything about lateness:
 * `rentRelaxationDays` extends the due date, and `lateChargesPerDay` is
 * charged for each day past that. The admin-wide LATE_FEE/GRACE_PERIOD are
 * only a fallback for callers that have no property to hand — using them for
 * a real tenancy showed the tenant a different figure to the one the bill
 * screens and the server had already worked out.
 *
 * @param {number} rentAmount - Base rent amount
 * @param {Date|string} dueDate - Rent due date
 * @param {object} customRates - Optional custom rates (overrides global)
 * @param {object} flatDetail - The property being rented (authoritative)
 * @returns {object} Payment breakdown with late fee if applicable
 */
export const calculateRentPayment = (
  rentAmount,
  dueDate,
  customRates = null,
  flatDetail = null
) => {
  const rates = customRates || COMMISSION_RATES;
  const amount = Number(rentAmount) || 0;
  const today = new Date();
  const due = parseDueDate(dueDate);

  const perDayCharge = Number(flatDetail?.lateChargesPerDay) || 0;
  const usePropertyRules = Boolean(flatDetail) && perDayCharge > 0;

  // Calculate the deadline: due date + the relaxation days that apply.
  const gracePeriod = usePropertyRules
    ? Number(flatDetail.rentRelaxationDays) || 0
    : rates.GRACE_PERIOD || 5;
  const gracePeriodEnd = new Date(due);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + gracePeriod);

  // Check if payment is late
  const isLate = today > gracePeriodEnd;
  const daysOverdue = isLate
    ? Math.floor((today - gracePeriodEnd) / (1000 * 60 * 60 * 24))
    : 0;

  // If service fee is disabled, no commission
  const isActive = rates.IS_ACTIVE !== false;
  const commissionRate = isActive ? rates.MONTHLY_RENT || 0 : 0;
  const commissionAmount = Math.round(amount * commissionRate);
  // Per-day when the property says so, otherwise the platform's flat fee.
  const lateFeeAmount = usePropertyRules
    ? daysOverdue * perDayCharge
    : rates.LATE_FEE || 0;
  const lateFee = isLate ? lateFeeAmount : 0;

  return {
    baseAmount: amount,
    commissionRate: commissionRate,
    commissionPercentage: `${commissionRate * 100}%`,
    commissionAmount: commissionAmount,
    lateFee: lateFee,
    totalTenantPays: amount + commissionAmount + lateFee,
    landlordReceives: amount,
    otsRevenue: commissionAmount + lateFee,
    isLate: isLate,
    daysOverdue: daysOverdue,
    gracePeriodEnd: gracePeriodEnd,
    gracePeriod: gracePeriod,
    type: "rent",
    isServiceActive: isActive,
  };
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount with Rs prefix
 */
export const formatCurrency = (amount) => {
  return `Rs ${Number(amount || 0).toLocaleString("en-PK")}`;
};

/**
 * Get payment status based on due date
 * @param {Date|string} dueDate - Payment due date
 * @param {boolean} isPaid - Whether payment is made
 * @param {object} customRates - Optional custom rates (overrides global)
 * @param {object} flatDetail - The property being rented (authoritative)
 * @returns {object} Status info with color and label
 */
export const getPaymentStatus = (
  dueDate,
  isPaid,
  customRates = null,
  flatDetail = null
) => {
  if (isPaid) {
    return { status: "paid", label: "Paid", color: "success" };
  }

  const rates = customRates || COMMISSION_RATES;
  // The property's relaxation days decide when "grace" ends, so the badge
  // agrees with the late charge the tenant is actually billed.
  const gracePeriod =
    flatDetail?.rentRelaxationDays != null
      ? Number(flatDetail.rentRelaxationDays) || 0
      : rates.GRACE_PERIOD ?? 5;

  const today = new Date();
  const due = parseDueDate(dueDate);
  const gracePeriodEnd = new Date(due);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + gracePeriod);

  if (today <= due) {
    return { status: "pending", label: "Due", color: "warning" };
  } else if (today <= gracePeriodEnd) {
    return { status: "grace", label: "Grace Period", color: "info" };
  } else {
    return { status: "overdue", label: "Overdue", color: "error" };
  }
};

/**
 * Calculate days until due or days overdue
 * @param {Date|string} dueDate - Payment due date
 * @returns {object} Days info
 */
export const getDaysInfo = (dueDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseDueDate(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return {
      daysUntilDue: diffDays,
      daysOverdue: 0,
      text: `${diffDays} days left`,
    };
  } else if (diffDays === 0) {
    return { daysUntilDue: 0, daysOverdue: 0, text: "Due Today" };
  } else {
    return {
      daysUntilDue: 0,
      daysOverdue: Math.abs(diffDays),
      text: `${Math.abs(diffDays)} days overdue`,
    };
  }
};

/**
 * Calculate combined commission for Advance + Rent
 * @param {number} advanceAmount
 * @param {number} rentAmount
 * @param {object} customRates
 * @returns {object}
 */
export const calculateAdvanceAndRentCommission = (
  advanceAmount,
  rentAmount,
  customRates = null
) => {
  const rates = customRates || COMMISSION_RATES;

  const advance = Number(advanceAmount) || 0;
  const rent = Number(rentAmount) || 0;

  const isActive = rates.IS_ACTIVE !== false;

  const advanceCommission = isActive
    ? Math.round(advance * (rates.ADVANCE || 0))
    : 0;

  const rentCommission = isActive
    ? Math.round(rent * (rates.MONTHLY_RENT || 0))
    : 0;

  return {
    advanceAmount: advance,
    rentAmount: rent,
    advanceCommission,
    rentCommission,
    totalCommission: advanceCommission + rentCommission,
    totalTenantPays: advance + rent + advanceCommission + rentCommission,
    landlordReceives: advance + rent,
    otsRevenue: advanceCommission + rentCommission,
    isServiceActive: isActive,
    commissionPercentage: `${
      rates.ADVANCE > 0 ? `Adv: ${(rates.ADVANCE || 0) * 100}% + ` : ""
    }Rent: ${(rates.MONTHLY_RENT || 0) * 100}%`,
    type: "advance_rent",
    commissionAmount: advanceCommission + rentCommission,
  };
};
